'use client'

import { Icon } from '@/components/ui/icon'
import { usePendingAppointments } from '@/hooks/use-pending-appointments'
import { RootState } from '@/lib/store'
import { setDrawerOpen } from '@/lib/store/features/booking/bookingSlice'
import { Button, ButtonProps, cn } from '@heroui/react'
import { createSelector } from '@reduxjs/toolkit'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// ReservationDrawer is now rendered at layout level via ReservationDrawerWrapper

const selectSelectedServiceIds = createSelector(
  [(state: RootState) => state.booking.persisted.selectedServiceIds],
  (selectedServiceIds) => selectedServiceIds ?? []
)

interface ReservationButtonProps extends Omit<ButtonProps, 'variant'> {
  /** 'prominent' = full button with text (public pages), 'subdued' = icon-only circle (app pages) */
  variant?: 'prominent' | 'subdued'
}

export function ReservationButton({ variant = 'subdued', ...props }: ReservationButtonProps) {
  const dispatch = useDispatch()
  const router = useRouter()
  const selectedServiceIds = useSelector(selectSelectedServiceIds)
  const serviceCount = selectedServiceIds.length

  const { hasPendingAppointments, isLoading: isPendingLoading } = usePendingAppointments()

  const handleClick = useCallback(() => {
    if (hasPendingAppointments) {
      dispatch(setDrawerOpen(true))
      return
    }
    if (serviceCount === 0) {
      router.push('/book')
      return
    }
    dispatch(setDrawerOpen(true))
  }, [dispatch, router, serviceCount, hasPendingAppointments])

  const shouldShowDot = hasPendingAppointments || serviceCount > 0
  const dotColor = hasPendingAppointments ? 'bg-warning' : 'bg-success'
  const dotContent = hasPendingAppointments ? '!' : serviceCount

  if (variant === 'prominent') {
    return (
      <Button
        onPress={handleClick}
        variant="primary"
        className="inline-flex items-center gap-1.5 text-sm rounded-full px-4 h-10 leading-none"
        // The one hot-pink button on the site (design-system templates, all
        // four agree): header Book Now, not the primary teal. Scoped to this
        // instance via inline style so it doesn't touch .button--primary's
        // global teal rule (globals.css) used by every other primary button.
        style={{
          '--button-bg': 'var(--btn-hot-bg)',
          '--button-bg-hover': 'var(--btn-hot-bg-hover)',
          '--button-bg-pressed': 'var(--btn-hot-bg-press)',
          '--button-fg': 'var(--btn-hot-fg)'
        } as React.CSSProperties}
        {...props}
      >
        <Icon icon="book-calendar" className="w-4 h-4" />
        Book Now
      </Button>
    )
  }

  // Subdued variant (icon-only, matching other header action buttons)
  return (
    <div className="relative w-10 h-10">
      <Button
        onPress={handleClick}
        {...props}
      >
        <Icon icon="book-calendar" className="w-5 h-5" />
      </Button>
      {shouldShowDot && !isPendingLoading && (
        <div
          className={cn(
            "absolute -top-1 -right-1 flex items-center justify-center rounded-full text-[9px] text-white font-medium z-10 min-w-[16px] h-[16px] px-0.5 animate-[badge-pop_200ms_ease-out] motion-reduce:animate-none",
            dotColor
          )}
          key={`${dotContent}`}
        >
          {dotContent}
        </div>
      )}
    </div>
  )
}
